import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { formatMoney } from '../../../../commodity/format/money';
import { KidEmpty } from '../../../../design-system/kid';
import { SearchFilters } from '../../api/courses';
import {
  ExploreCourse,
  ExploreResponse,
  getExplore,
  registerInterest,
} from '../../api/learner-explore';
import CatalogFilters from '../../components/CatalogFilters';
import { describeSchedule } from '../../domain/catalog';
import { filtersFromParams, filtersToParams, narrowingFilters } from '../../domain/catalog-context';
import { REQUEST_STATE_WORDS, gradeRangeWords } from '../../learner/requestWords';
import { courseZone, formatDayAndTime } from '../../time/zonedTime';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: ExploreResponse };
type Interest = 'idle' | 'sending' | 'sent' | 'failed';

/**
 * UC-S03 — the catalog, from the child's space (DEC-S2-S03-01..08).
 *
 * The same supply, taxonomy, IDs and values the Parent reads; the server adds
 * only what depends on who is looking. Fit is the default view and the Student
 * can widen it (DEC-S2-S03-04), which changes what is shown and nothing about
 * what can be requested. The filter panel is the Parent's one component, with
 * one way to commit it on any width (DEC-S2-S03-06), and the address bar holds
 * the result. No ranking, no trust score, no Saved (DEC-S2-S03-08).
 */
const LearnerExplore: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const everything = searchParams.get('fit') === 'all';
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [more, setMore] = useState<'idle' | 'loading' | 'failed'>('idle');
  const [staged, setStaged] = useState<SearchFilters | null>(null);
  const [interest, setInterest] = useState<Interest>('idle');

  const query = useMemo(
    () => ({ ...filters, ...(everything ? { fit: 'all' as const } : {}) }),
    [filters, everything],
  );

  const fetchFirst = useCallback(() => {
    setLoad({ status: 'loading' });
    setInterest('idle');
    getExplore(query)
      .then((data) => setLoad({ status: 'ready', data }))
      .catch(() => setLoad({ status: 'error' }));
  }, [query]);

  useEffect(fetchFirst, [fetchFirst]);

  const commit = (next: SearchFilters, widen = everything) => {
    const params = filtersToParams(next);
    if (widen) params.set('fit', 'all');
    setSearchParams(params, { replace: true });
    setStaged(null);
  };

  const loadMore = (nextToken: string) => {
    setMore('loading');
    getExplore({ ...query, nextToken })
      .then((page) => {
        setLoad((current) =>
          current.status === 'ready'
            ? {
                status: 'ready',
                data: { ...page, items: [...current.data.items, ...page.items] },
              }
            : current,
        );
        setMore('idle');
      })
      .catch(() => setMore('failed'));
  };

  // DEC-S2-S03-07: a handoff, reason-coded. Nothing is promised and nothing
  // is recommended in its place.
  const tellUs = () => {
    setInterest('sending');
    registerInterest({ reason: 'NO_SUPPLY', subject: filters.subject, gradeLevel: filters.level })
      .then(() => setInterest('sent'))
      .catch(() => setInterest('failed'));
  };

  const filtered = narrowingFilters(filters).length > 0;

  return (
    <>
      <h1 className="hi">Explorar clases</h1>
      <p className="hi-s">
        {everything ? 'Estás viendo todas las clases.' : 'Estas son las clases para tu grado.'}
      </p>

      <p>
        <button
          type="button"
          className="lrn-link lrn-btn"
          aria-expanded={staged !== null}
          onClick={() => setStaged(staged === null ? filters : null)}
        >
          {filtered ? `Filtros (${narrowingFilters(filters).length})` : 'Filtros'}
        </button>
        <button
          type="button"
          className="lrn-link lrn-btn"
          onClick={() => commit(filters, !everything)}
        >
          {everything ? 'Ver solo las de mi grado' : 'Ver todas las clases'}
        </button>
      </p>

      {staged !== null && (
        <div className="lrn-status" role="group" aria-label="Filtros de clases">
          <CatalogFilters value={staged} onChange={setStaged} />
          <div className="kid-dialog-b">
            <button type="button" className="cb cb-p" onClick={() => commit(staged)}>
              Aplicar
            </button>
            <button type="button" className="cb cb-o" onClick={() => commit({ sortBy: 'newest' })}>
              Quitar filtros
            </button>
          </div>
        </div>
      )}

      {load.status === 'loading' && (
        <p className="hi-s" role="status">
          Cargando clases…
        </p>
      )}

      {load.status === 'error' && (
        <div className="lrn-status" role="alert">
          <h2>No pudimos cargar las clases</h2>
          <p>No es culpa tuya. Prueba otra vez en un momento.</p>
          <button className="ag-b" onClick={fetchFirst}>
            Intentar otra vez
          </button>
        </div>
      )}

      {load.status === 'ready' && load.data.items.length === 0 && (
        <>
          <KidEmpty
            icon="🔎"
            title="No encontramos clases aquí"
            detail={
              filtered || !everything
                ? 'Puedes cambiar los filtros o ver todas las clases.'
                : 'Todavía no hay clases publicadas.'
            }
          />
          {interest === 'sent' ? (
            <p className="hi-s" role="status">
              Listo. Ya sabemos que te interesa. Esto no aparta ningún lugar.
            </p>
          ) : (
            <p>
              <button
                type="button"
                className="ag-b"
                disabled={interest === 'sending'}
                onClick={tellUs}
              >
                Quiero clases de esto
              </button>
            </p>
          )}
          {interest === 'failed' && (
            <p className="hi-s" role="alert">
              No pudimos guardarlo. Prueba otra vez.
            </p>
          )}
        </>
      )}

      {load.status === 'ready' && load.data.items.length > 0 && (
        <>
          <div className="grid">
            {load.data.items.map((course) => (
              <ExploreCard key={course.listingId} course={course} />
            ))}
          </div>
          {load.data.nextToken && (
            <p>
              <button
                type="button"
                className="ag-b"
                disabled={more === 'loading'}
                onClick={() => loadMore(load.data.nextToken as string)}
              >
                {more === 'failed' ? 'Intentar otra vez' : 'Ver más clases'}
              </button>
            </p>
          )}
        </>
      )}
    </>
  );
};

const FIT_WORDS: Record<ExploreCourse['fit'], string | null> = {
  FIT: 'Para tu grado',
  OUT_OF_RANGE: 'No es para tu grado',
  UNKNOWN: null,
};

/**
 * DEC-S2-S03-05 — the card's "next/availability context", from the offer's
 * materialized meetings in the course's zone, the same fact the Parent's card
 * reads. A full class, one by appointment and one whose dates could not be read
 * each say so; none of them falls back to a date.
 */
const NextMeeting: React.FC<{ course: ExploreCourse }> = ({ course }) => {
  if (course.nextAvailableAt && course.timezone) {
    const tz = courseZone(course.timezone);
    return (
      <p className="cc-w">
        Próxima clase: {formatDayAndTime(course.nextAvailableAt, tz)} ({tz.zone})
      </p>
    );
  }
  if (course.scheduleType !== 'RECURRING' || course.availability === 'FULL') return null;
  return <p className="cc-w">Fechas no disponibles por ahora</p>;
};

/** DEC-S2-S03-05: the card, from facts the server gave. See Details is its one action. */
const ExploreCard: React.FC<{ course: ExploreCourse }> = ({ course }) => {
  const range = gradeRangeWords(course.gradeLevel);

  return (
    <article className="cc" aria-label={course.title}>
      <div className="cc-b">
        <h2 className="cc-t">{course.title}</h2>
        <p className="cc-w">
          {course.teacherName ? `Con ${course.teacherName}` : 'Profesor'}
          {course.teacherVerified ? ' · Verificado' : ''}
        </p>
        <p className="cc-w">
          {!course.rating
            ? 'Valoración no disponible'
            : course.rating.count === 0
            ? 'Sin reseñas todavía'
            : `★ ${course.rating.average.toFixed(1)} (${course.rating.count})`}
        </p>
        {FIT_WORDS[course.fit] && <span className="lrn-tag">{FIT_WORDS[course.fit]}</span>}
        {range && <p className="cc-w">Grados: {range}</p>}
        <p className="cc-when">
          {course.scheduleType === 'RECURRING' && course.schedule
            ? describeSchedule(course.schedule)
            : 'Horario por cita'}
          {course.durationMode === 'FIXED' && course.durationWeeks
            ? ` · ${course.durationWeeks} semanas`
            : ''}
        </p>
        <NextMeeting course={course} />
        <p className="cc-w">
          {course.duration} min · {formatMoney(course.price, course.currency)} por sesión
        </p>
        {course.availability === 'FULL' && <span className="lrn-tag wait">Sin lugares</span>}
        {course.request && (
          <span className="lrn-tag wait">{REQUEST_STATE_WORDS[course.request.state]}</span>
        )}
        <p>
          <Link
            className="lrn-link"
            to={`/student/explore/${encodeURIComponent(course.listingId)}`}
          >
            Ver detalles
          </Link>
        </p>
      </div>
    </article>
  );
};

export default LearnerExplore;
